/**
 * AddSubcontractorModal
 * Modal for adding a subcontractor to an assignment that's already confirmed/in progress.
 * Allows admin to choose the TG status and optionally send an SMS.
 */

import { useState } from 'react'
import { X, Loader2, Leaf, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react'
import type { TgWorkStatus } from '../../types/statusTypes'

export interface AddSubcontractorResult {
  tgStatus: TgWorkStatus
  sendSms: boolean
}

interface AddSubcontractorModalProps {
  isOpen: boolean
  onClose: () => void
  subcontractorName: string
  subcontractorPhone: string | null
  assignmentNumber?: number
  assignmentTitle: string
  assignmentDate?: string
  assignmentTime?: string
  currentAssignmentStatus: string
  onConfirm: (result: AddSubcontractorResult) => Promise<void>
}

export function AddSubcontractorModal({
  isOpen,
  onClose,
  subcontractorName,
  subcontractorPhone,
  assignmentNumber,
  assignmentTitle,
  assignmentDate,
  assignmentTime,
  currentAssignmentStatus,
  onConfirm
}: AddSubcontractorModalProps) {
  // Determine the correct status based on assignment status - no choice needed
  const getAutoStatus = (): TgWorkStatus => {
    if (currentAssignmentStatus === 'pagaende' || currentAssignmentStatus === 'granskning') {
      // In-progress/review: TG must be "ej_rapporterad" - they haven't reported yet
      return 'ej_rapporterad'
    }
    // planerad: TG is directly assigned
    return 'tilldelad'
  }

  const autoStatus = getAutoStatus()
  const statusLabel = autoStatus === 'ej_rapporterad' ? 'Ej rapporterad' : 'Tilldelad'
  const [sendSms, setSendSms] = useState(!!subcontractorPhone)
  const [showSmsPreview, setShowSmsPreview] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const formatTime = (time: string | null | undefined) => {
    if (!time) return ''
    return time.substring(0, 5)
  }

  const getAssignmentIdentifier = () => {
    if (assignmentNumber) {
      return `#${assignmentNumber} ${assignmentTitle}`
    }
    return assignmentTitle
  }

  const getSmsMessage = () => {
    const identifier = getAssignmentIdentifier()
    const dateTimeStr = assignmentDate
      ? `${assignmentDate}${assignmentTime ? ' kl ' + formatTime(assignmentTime) : ''}`
      : ''

    if (autoStatus === 'tilldelad') {
      return `Hej! Du har blivit tilldelad uppdraget ${identifier}${dateTimeStr ? ` (${dateTimeStr})` : ''}. Detaljer finns i appen.`
    }
    if (autoStatus === 'ej_rapporterad') {
      return `Hej! Du har lagts till på uppdraget ${identifier}${dateTimeStr ? ` (${dateTimeStr})` : ''} som pågår. Glöm inte att rapportera när du är klar.`
    }
    return ''
  }

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm({
        tgStatus: autoStatus,
        sendSms: sendSms && !!subcontractorPhone
      })
      onClose()
    } catch (error) {
      console.error('Error adding subcontractor:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getStatusLabel = () => {
    switch (currentAssignmentStatus) {
      case 'planerad': return 'Planerat'
      case 'pagaende': return 'Pågående'
      case 'granskning': return 'Granskning'
      default: return currentAssignmentStatus
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-nature-200 dark:border-nature-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
                <Leaf className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  Lägg till trädgårdsmästare
                </h3>
                <p className="text-sm text-nature-500 dark:text-nature-400">
                  Uppdraget är {getStatusLabel().toLowerCase()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-5 overflow-y-auto flex-1">
          {/* TG info */}
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary-200 dark:bg-primary-700 flex items-center justify-center">
                <Leaf className="h-5 w-5 text-primary-700 dark:text-primary-300" />
              </div>
              <div>
                <p className="font-medium text-nature-900 dark:text-nature-100">{subcontractorName}</p>
                {subcontractorPhone && (
                  <p className="text-sm text-nature-500 dark:text-nature-400">{subcontractorPhone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Status info - automatic based on assignment status */}
          <div className="p-3 bg-nature-100 dark:bg-nature-700/30 rounded-lg">
            <p className="text-sm text-nature-700 dark:text-nature-300">
              Trädgårdsmästaren kommer att sättas som <span className="font-medium">{statusLabel}</span>.
            </p>
          </div>

          {/* SMS option */}
          {subcontractorPhone && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="h-4 w-4 text-primary-600 rounded"
                  />
                  <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
                    Skicka SMS till trädgårdsmästaren
                  </span>
                </label>
                {sendSms && (
                  <button
                    type="button"
                    onClick={() => setShowSmsPreview(!showSmsPreview)}
                    className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <MessageSquare className="h-3 w-3" />
                    Förhandsgranska
                    {showSmsPreview ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>
                )}
              </div>

              {sendSms && showSmsPreview && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">
                    SMS till {subcontractorPhone}
                  </p>
                  <div className="bg-white dark:bg-nature-800 rounded p-2 border border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-nature-700 dark:text-nature-300 font-mono">
                      {getSmsMessage()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {!subcontractorPhone && (
            <p className="text-sm text-nature-500 dark:text-nature-400 italic">
              Inget telefonnummer registrerat - kan inte skicka SMS
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900 flex-shrink-0">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="btn-outline px-4 py-2"
              disabled={isSubmitting}
            >
              Avbryt
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Lägger till...</span>
                </>
              ) : (
                <span>Lägg till</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
